// TestDriveForm.js
import React from "react";

export default function TestDriveForm({
  vehicleReg,
  onCancel,
  onSubmitted,
  isPosting,
  profile,
  setProfile,
  formId,
}) {
  const [date, setDate] = React.useState("");
  const [time, setTime] = React.useState("");
  const [errors, setErrors] = React.useState({});

  const { fullName = "", telephone = "", email = "" } = profile || {};

  let title = "Book a test drive";
  if (formId === "videoWalkaround") {
    title = "Book a video walkaround";
  } else if (formId === "oneToOneSalesCall") {
    title = "Book a one to one sales call";
  } else if (formId === "carViewingAppointment") {
    title = "Book a car viewing appointment";
  }

  // ---- Tomorrow helpers ----
  const pad = (n) => String(n).padStart(2, "0");
  const toLocalDate = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  // Calculate tomorrow's date
  const now = new Date();
  const TOMORROW_DT = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1
  );
  const TOMORROW_DATE = toLocalDate(TOMORROW_DT);

  function validateDateTime(selectedDate, selectedTime) {
    const out = {};
    if (!selectedDate) out.date = "Choose a date";
    if (!selectedTime) out.time = "Choose a time";
    if (out.date || out.time) return { ok: false, ...out };

    // Only allow tomorrow or any future date
    if (selectedDate < TOMORROW_DATE) {
      const [yyyy, mm, dd] = TOMORROW_DATE.split("-");
      return {
        ok: false,
        date: `Date must be tomorrow (${dd}/${mm}/${yyyy}) or later.`,
      };
    }

    // Validate time format
    const selected = new Date(`${selectedDate}T${selectedTime}`);
    if (isNaN(selected.getTime())) {
      return { ok: false, time: "Invalid time selected." };
    }

    return { ok: true };
  }

  // Use TOMORROW_DATE as min for date input
  const MIN_DATE = TOMORROW_DATE;
  const MIN_TIME = "09:00";

  function validate() {
    const errs = {};
    if (!fullName.trim()) errs.fullName = "Please enter your full name";
    if (!telephone.trim()) errs.telephone = "Please enter a phone number";
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email))
      errs.email = "Enter a valid email";

    const dtCheck = validateDateTime(date, time);
    if (!dtCheck.ok) {
      if (dtCheck.date) errs.date = dtCheck.date;
      if (dtCheck.time) errs.time = dtCheck.time;
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const onDateChange = (e) => {
    const nextDate = e.target.value;
    setDate(nextDate);

    if (time) {
      const check = validateDateTime(nextDate, time);
      setErrors((prev) => ({
        ...prev,
        date: check.ok ? "" : check.date || prev.date,
        time: check.ok ? "" : check.time || prev.time,
      }));
    } else {
      setErrors((prev) => ({ ...prev, date: "" }));
    }
  };

  const onTimeChange = (e) => {
    const nextTime = e.target.value;
    setTime(nextTime);
    if (date) {
      const check = validateDateTime(date, nextTime);
      setErrors((prev) => ({
        ...prev,
        date: check.ok ? "" : check.date || prev.date,
        time: check.ok ? "" : check.time || prev.time,
      }));
    } else {
      setErrors((prev) => ({ ...prev, time: "" }));
    }
  };

  const isMinDate = date === MIN_DATE;

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      type: "testDrive",
      vehicleReg,
      fullName,
      telephone,
      email,
      date,
      time,
      submittedAt: new Date().toISOString(),
      formId: formId || "testDrive",
    };

    onSubmitted?.(payload);
  }

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <div style={formStyles.header}>{title}</div>

      <label style={formStyles.label}>
        Full name
        <input
          style={formStyles.input}
          value={fullName}
          onChange={(e) =>
            setProfile((p) => ({ ...p, fullName: e.target.value }))
          }
        />
        {errors.fullName && (
          <div style={formStyles.error}>{errors.fullName}</div>
        )}
      </label>

      <label style={formStyles.label}>
        Telephone
        <input
          style={formStyles.input}
          value={telephone}
          onChange={(e) =>
            setProfile((p) => ({ ...p, telephone: e.target.value }))
          }
        />
        {errors.telephone && (
          <div style={formStyles.error}>{errors.telephone}</div>
        )}
      </label>

      <label style={formStyles.label}>
        Email
        <input
          type="email"
          style={formStyles.input}
          value={email}
          onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
        />
        {errors.email && <div style={formStyles.error}>{errors.email}</div>}
      </label>

      <div style={{ display: "flex", gap: 8 }}>
        <label style={{ ...formStyles.label, flex: 1 }}>
          Date
          <input
            type="date"
            style={formStyles.input}
            value={date}
            onChange={onDateChange}
            min={MIN_DATE}
          />
          {errors.date && <div style={formStyles.error}>{errors.date}</div>}
        </label>

        <label style={{ ...formStyles.label, flex: 1 }}>
          Time
          <input
            type="time"
            style={formStyles.input}
            value={time}
            onChange={onTimeChange}
            disabled={!date}
            {...(isMinDate ? { min: MIN_TIME } : {})}
          />
          {errors.time && <div style={formStyles.error}>{errors.time}</div>}
        </label>
      </div>

      <div style={formStyles.actions}>
        <button
          type="button"
          onClick={onCancel}
          style={formStyles.secondaryBtn}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPosting}
          style={formStyles.primaryBtn}
        >
          {isPosting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </form>
  );
}

const formStyles = {
  form: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
  },
  header: {
    fontWeight: 700,
    marginBottom: 8,
  },
  label: {
    display: "block",
    fontSize: 12,
    color: "#334155",
    marginBottom: 8,
  },
  input: {
    width: "100%",
    marginTop: 4,
    padding: "8px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
  },
  error: {
    marginTop: 4,
    color: "#b91c1c",
    fontSize: 12,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  },
  secondaryBtn: {
    background: "#f1f5f9",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  },
};
